import React, { useMemo } from "react";
import {
  ArrowRight, BarChart3, BookOpen, Check, Circle, ClipboardCheck, Clock, FileDown, FileText,
  HelpCircle, ListChecks, PencilLine, Plus, ScrollText, Upload,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import NumaMascot from "@/components/NumaMascot";
import { useAuth } from "@/contexts/AuthContext";
import { PROJECT_LABELS, agree, unitPlural } from "@/lib/projectTypes";

/*
  What there is to do here, and what it costs.

  `ai` says whether choosing this sends text to a model - false, null for the
  ones that are partly assisted, true for the ones that always are. `modes`
  says where the card belongs: a manuscript brought in to be reviewed has no
  use for a page about what to write next, and a project being written has no
  review to open yet. A card that leads nowhere is worse than no card.
*/
const ACTIONS = [
  {
    id: "deep-review", key: "deep-review",
    ai: null, title: "Revisione", icon: ClipboardCheck,
    desc: "Ortografia, riferimenti, struttura, leggibilità e stile; le correzioni si applicano una per volta o in blocco",
  },
  {
    id: "start", key: "start", modes: ["write"],
    ai: true, title: "Da dove iniziare?", icon: HelpCircle,
    desc: "Scaletta e domande guida per una sezione nuova",
  },
  {
    id: "writing", key: "writing",
    ai: false, title: "Scrivere", icon: PencilLine,
    desc: "Editor a pagine, con i suggerimenti bibliografici mentre scrivi",
  },
  {
    id: "sources", key: "sources",
    ai: null, title: "Fonti", icon: BookOpen,
    desc: "PDF anche scansionati, metadati da DOI e Crossref, bibliografia negli stili usati",
  },
  {
    id: "stats", key: "stats", modes: ["write"],
    ai: false, title: "Andamento", icon: BarChart3,
    desc: "Parole, sezioni completate e ritmo di lavoro nel tempo",
  },
  {
    id: "history", key: "history", modes: ["review"],
    ai: false, title: "Versioni", icon: Clock,
    desc: "Ogni correzione applicata lascia la versione precedente: si torna indietro quando serve",
  },
  {
    id: "export", key: "export",
    ai: false, title: "Esportare", icon: FileDown,
    desc: "DOCX o PDF con la struttura, gli stili e la bibliografia",
  },
];

function greeting(user) {
  const hour = new Date().getHours();
  const timeOfDay = hour < 13 ? "Buongiorno" : hour < 18 ? "Buon pomeriggio" : "Buonasera";
  const name = user?.name?.trim();
  if (!name) return timeOfDay;
  const shown = user?.title ? `${user.title} ${name.split(" ").slice(-1)[0]}` : name.split(" ")[0];
  return `${timeOfDay}, ${shown}`;
}

function Stat({ value, label }) {
  return (
    <div>
      <p className="font-serif-display text-[26px] font-semibold leading-none tabular-nums text-ink">{value}</p>
      <p className="numa-eyebrow mt-1.5">{label}</p>
    </div>
  );
}

/** Dove sta nell'originale: «pp. 12–18». Serve a chi cerca, cioè in revisione. */
function paginaDi(chapter) {
  const da = Number(chapter?.start_page) || 0;
  const a = Number(chapter?.end_page) || da;
  if (!da) return "";
  return a > da ? `pp. ${da}–${a}` : `p. ${da}`;
}

/** Quanto è lunga: «4 pag.». Serve a chi scrive, cioè accanto alla percentuale. */
function quantePagine(chapter) {
  const da = Number(chapter?.start_page) || 0;
  const a = Number(chapter?.end_page) || da;
  if (!da) return "";
  return `${Math.max(1, a - da + 1)} pag.`;
}

function OutlineRow({ chapter, index, mode = "write" }) {
  const Icon = chapter.status === "completed" ? Check : Circle;
  const tone =
    chapter.status === "completed"
      ? "text-ok"
      : chapter.status === "in_progress" || chapter.status === "draft"
      ? "text-brand"
      : "text-ink-subtle";
  return (
    <li className="flex items-center gap-3 border-b border-line py-2 last:border-0">
      <span className="w-5 shrink-0 text-2xs tabular-nums text-ink-subtle">{index + 1}</span>
      <Icon className={`h-3.5 w-3.5 shrink-0 ${tone}`} aria-hidden="true" />
      <span className="min-w-0 flex-1 truncate text-[13.5px] text-ink">{chapter.title}</span>
      {/* Su un documento in revisione l'avanzamento non esiste: è finito. Al
          suo posto le pagine dell'originale, che sono quello che si cerca. */}
      {mode === "review" ? (
        <span className="shrink-0 text-2xs tabular-nums text-ink-subtle">{paginaDi(chapter)}</span>
      ) : (
        <>
          <span className="w-24 shrink-0">
            <span className="numa-progress-track">
              <span className="numa-progress-fill block" style={{ width: `${chapter.progress}%` }} />
            </span>
          </span>
          <span className="shrink-0 text-right text-2xs tabular-nums text-ink-subtle">
            {[`${chapter.progress}%`, quantePagine(chapter)].filter(Boolean).join(" · ")}
          </span>
        </>
      )}
    </li>
  );
}

export default function HomeView({ thesis, onAction, onOpenProject, activities = [], caricando = false }) {
  const { user } = useAuth();
  const hasProject = Boolean(thesis?.thesis_id);
  // Projects created before the two modes existed are projects being written.
  const mode = thesis?.project_mode === "review" ? "review" : "write";
  const completion = thesis?.completion_percentage || 0;
  const units = unitPlural(thesis?.project_type);

  const wordsTotal = useMemo(
    () =>
      thesis?.chapters?.reduce(
        (acc, c) => acc + (c.content?.replace(/<[^>]+>/g, "").split(/\s+/).filter(Boolean).length || 0),
        0
      ) || 0,
    [thesis]
  );

  const completedCount = thesis?.chapters?.filter((c) => c.status === "completed").length || 0;
  const nextChapter = thesis?.chapters?.find((c) => c.status !== "completed");

  /*
    Finché i progetti sono per strada non si dice che non ce ne sono.

    Ricaricando la pagina, «Non hai ancora un progetto aperto» compariva per
    primo e restava lì qualche secondo: è la frase che fa pensare di aver perso
    settimane di lavoro, e la si leggeva ogni volta.
  */
  if (!hasProject && caricando) {
    return (
      <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="home-view">
        <div className="mx-auto flex max-w-lg flex-col items-center px-6 pt-[18vh] text-center">
          <NumaMascot size={112} className="numa-mark" variant={6} />
          <p className="mt-7 text-[14.5px] text-ink-muted" data-testid="home-loading">
            Sto aprendo i tuoi progetti…
          </p>
          <div className="mt-6 w-full space-y-2">
            {[0, 1].map((i) => <div key={i} className="numa-shimmer h-16 rounded-md" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!hasProject) {
    return (
      <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="home-view">
        <div className="mx-auto flex max-w-lg flex-col items-center px-6 pt-[18vh] text-center">
          <NumaMascot size={112} className="numa-mark" variant={6} />
          <h1 className="mt-7 font-serif-display text-2xl font-semibold text-ink">
            {greeting(user)}
          </h1>
          <p className="mt-2.5 text-[14.5px] leading-relaxed text-ink-muted">
            Non hai ancora un progetto aperto. Creane uno scegliendo il tipo di documento,
            oppure importa un file esistente: NUMA ne riconosce la struttura.
          </p>
          <div className="mt-7 flex gap-2">
            <Button data-testid="home-open-project" onClick={() => onAction?.("create")}>
              <Plus className="h-4 w-4" /> Nuovo progetto
            </Button>
            <Button data-testid="home-import-empty" variant="outline" onClick={() => onAction?.("import")}>
              <Upload className="h-4 w-4" /> Importa documento
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-y-auto bg-paper numa-fade-in" data-testid="home-view">
      <div className="mx-auto max-w-5xl px-6 py-9 lg:px-10">
        {/* Header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="numa-eyebrow">{greeting(user)}</p>
            <h1
              data-testid="home-project-title"
              className="mt-1.5 font-serif-display text-[28px] font-semibold leading-tight text-ink"
            >
              {thesis.title}
            </h1>
            <p className="numa-meta mt-1">
              {PROJECT_LABELS[thesis.project_type] || "Documento"}
              {thesis.subtitle ? ` · ${thesis.subtitle}` : ""}
            </p>
          </div>
          <Button data-testid="home-new-btn" variant="outline" onClick={() => onAction?.("create")}>
            <Plus className="h-4 w-4" /> Nuovo progetto
          </Button>
        </header>

        {/* Figures */}
        <section className="mt-8 grid grid-cols-2 gap-6 border-y border-line py-5 sm:grid-cols-4">
          <Stat value={wordsTotal.toLocaleString("it-IT")} label="Parole" />
          <Stat value={thesis.chapters?.length || 0} label={units} />
          <Stat value={completedCount} label="Completate" />
          <Stat value={`${completion}%`} label="Avanzamento" />
        </section>

        {/* Next step */}
        <section className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-md border border-line bg-surface px-4 py-3.5">
          <div className="min-w-0">
            <p className="numa-eyebrow">Prossimo passo</p>
            <p className="mt-1 truncate text-[15px] text-ink">
              {nextChapter
                ? nextChapter.title
                : `Tutti i ${units.toLowerCase()} risultano ${agree(thesis?.project_type, "completati", "completate")}`}
            </p>
          </div>
          <Button data-testid="home-open-project" onClick={onOpenProject}>
            Apri nell'editor <ArrowRight className="h-4 w-4" />
          </Button>
        </section>

        {/* Actions */}
        <section className="mt-9">
          <h2 className="numa-eyebrow">Attività</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {ACTIONS.filter((a) => !a.modes || a.modes.includes(mode)).map(
              ({ id, key, title, desc, icon: Icon, ai }) => (
              <button
                key={id}
                type="button"
                data-testid={`home-action-${id}`}
                onClick={() => onAction?.(key)}
                className="group rounded-md border border-line bg-surface p-3.5 text-left transition-colors hover:border-line-strong hover:bg-surface-2"
              >
                <Icon className="h-4 w-4 text-ink-muted transition-colors group-hover:text-brand" />
                <p className="mt-2.5 text-[13.5px] font-medium text-ink">{title}</p>
                <p className="mt-0.5 text-2xs leading-snug text-ink-muted">{desc}</p>
                <p className="mt-1.5 text-2xs text-ink-subtle">
                  {ai === true ? "usa l'AI" : ai === null ? "in parte AI" : "senza AI"}
                </p>
              </button>
            ))}
          </div>
        </section>

        {/* Outline + recent */}
        <div className="mt-9 grid gap-8 lg:grid-cols-[1.4fr_1fr]">
          <section>
            <div className="flex items-baseline justify-between">
              <h2 className="numa-eyebrow">Struttura</h2>
              <Button
                data-testid="home-manage-plan"
                variant="ghost"
                size="sm"
                onClick={() => onAction?.("plan")}
                className="h-7 text-[12.5px]"
              >
                Modifica <ArrowRight className="h-3 w-3" />
              </Button>
            </div>
            <ul className="mt-2">
              {thesis.chapters?.map((c, i) => (
                <OutlineRow key={c.id} chapter={c} index={i} mode={mode} />
              ))}
            </ul>
          </section>

          <section>
            <h2 className="numa-eyebrow">Attività recente</h2>
            <div className="mt-2" data-testid="home-activity">
              {activities.length === 0 ? (
                <p className="py-2 text-[13px] text-ink-muted">Nessuna attività registrata.</p>
              ) : (
                <ul>
                  {activities.slice(0, 6).map((a, i) => (
                    <li key={i} className="flex items-start gap-2.5 border-b border-line py-2 last:border-0">
                      {a.kind === "import" ? (
                        <FileText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" />
                      ) : a.kind === "summary" ? (
                        <ScrollText className="mt-0.5 h-3.5 w-3.5 shrink-0 text-assistant" />
                      ) : (
                        <PencilLine className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[13px] text-ink">{a.title}</p>
                        <p className="text-2xs text-ink-subtle">{a.when}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
